import { Contacts } from './Contacts'

type RefLead = { uuid: string; latest_visit: number | string }

// latest_visit may itself hold colons, so the parts after it are matched from the end.
const MANUAL_SEND_REF = /^wf:(\d+):([^:]+):manual:[^:]+(?::nofilter)?:[^:]+:(?:email|phone)$/
const WORKFLOW_REF = /^wf:(\d+):([^:]+):.*?(:nofilter)?:[^:]+:(?:email|phone)$/
const EXPORT_REF = /^export:(\d+):(\d+):[^:]+:(?:email|phone)$/
const REVEAL_REF = /^reveal:(\d+|none):.*:[^:]+:(?:email|phone)$/
const OUTREACH_CONNECTION_REF = /^aec:([^:]+):[^:]+:.*:\d+:[^:]+:(?:email|phone)$/
const SETTLE_REF = /^settle:(?:([a-z_]+):)?/
const DOMAIN_OF_REF = /:([^:]+):(?:email|phone)$/
const EXPORT_CHUNK_REF = /^export:(\d+):\d+$/
const NO_FILTER_SUFFIX = /:nofilter$/

const autoEngageSource = (source?: string): Contacts.AutoEngageSource =>
  Contacts.AUTO_ENGAGE_SOURCES.find(known => known === source) ?? 'auto_engage'

// Every reservation ref is built and read here, so a format change can't break the Bunia activity panel unseen.
export namespace ContactRefs {
  export const SEPARATOR = ':'

  // What search reads out of a stored ref in SQL (SUBSTRING_INDEX), counted from 1.
  export const SEGMENTS = { workflowLead: 3, domainFromEnd: 2 }

  export const meterSuffix = (meter: Contacts.CreditMeter) => `:${meter}`

  export const workflow = (workflowId: number, lead: RefLead) => `wf:${workflowId}:${lead.uuid}:${lead.latest_visit}`

  export const withoutFilters = (ref: string) => `${ref}:nofilter`

  // A user's "Export to connector": one ref per send, under the workflow's prefix.
  export const manualSend = (workflowId: number, leadUuid: string, sendId: string) =>
    `wf:${workflowId}:${leadUuid}:manual:${sendId}`

  export const exportChunk = (exportId: number, chunk: number) => `export:${exportId}:${chunk}`

  export const exportTrace = (exportId: number) => `export:${exportId}`

  // Two slots can hold the same campaign with different buyer personas, so the slot is part of the ref.
  export const outreachConnection = (campaignId: string, slot: number, lead: RefLead) =>
    `aec:${campaignId}:${lead.uuid}:${lead.latest_visit}:${slot}`

  // One ref per user and people asked for, so a double click shares one reservation.
  export const reveal = (userId: number | undefined, ids: string[]) =>
    `reveal:${userId ?? 'none'}:${[...ids].sort().join(',')}`

  export const settle = (source: Contacts.UnlockSource, id: string) => `settle:${source}:${id}`

  // Contacts hashes the result when it is too long for its column.
  export const perMeter = (ref: string, domain: string, meter: Contacts.CreditMeter) =>
    `${ref}:${domain}${meterSuffix(meter)}`

  export const HASHED_PREFIX = 'sha256:'

  // The event a request ref belongs to (before perMeter): a whole export, a lead's visit or a send with its retry.
  export const requestKey = (ref: string) => {
    const chunk = ref.match(EXPORT_CHUNK_REF)
    return chunk ? exportTrace(Number(chunk[1])) : ref.replace(NO_FILTER_SUFFIX, '')
  }

  export const originOf = (ref: string): Contacts.RequestOrigin => {
    const manual = ref.match(MANUAL_SEND_REF)
    if (manual) return { kind: 'manual_send', workflowId: Number(manual[1]), leadUuid: manual[2] }
    const workflow = ref.match(WORKFLOW_REF)
    if (workflow) {
      return { kind: 'workflow', workflowId: Number(workflow[1]), leadUuid: workflow[2], noFilter: !!workflow[3] }
    }
    const exported = ref.match(EXPORT_REF)
    if (exported) return { kind: 'export', exportId: Number(exported[1]), chunk: Number(exported[2]) }
    const reveal = ref.match(REVEAL_REF)
    if (reveal) return { kind: 'reveal', userId: reveal[1] === 'none' ? null : Number(reveal[1]) }
    const connection = ref.match(OUTREACH_CONNECTION_REF)
    if (connection) return { kind: 'outreach_connection', campaignId: connection[1] }
    // Rows from before the source was recorded are `settle:<uuid>`.
    const settled = ref.match(SETTLE_REF)
    if (settled) return { kind: 'auto_engage', source: autoEngageSource(settled[1]) }
    if (ref.startsWith(HASHED_PREFIX)) return { kind: 'hashed' }
    return { kind: 'other' }
  }

  export const domainOf = (ref: string) => ref.match(DOMAIN_OF_REF)?.[1]

  export const searchPrefix = {
    workflow: (workflowId?: number) => (workflowId == null ? 'wf:' : `wf:${workflowId}:`),
    exportChunks: (exportId: number) => `export:${exportId}:`,
    reveal: (userId: number) => `reveal:${userId}:`,
  }
}
